import { Injectable, Inject } from '@angular/core';

const USER_STATE_KEY = "BASE_USER_STATE_KEY";

interface CacheConfig {
  maxValueSize: number;
  retryMaxSize: number;
  blockedPatterns: string[];
  enableLogging: boolean;
}

export class CacheService {
    private config: CacheConfig = {
        maxValueSize: 500 * 1024, // 500KB
        retryMaxSize: 100 * 1024,  // 100KB
        blockedPatterns: ['search/', 'all_records', '_records'],
        enableLogging: true
    };

    constructor(private cacheEnabled: boolean = false) {}

    public set(key: string, value: any): void {
        if (!this.shouldStoreKey(key)) {
            return;
        }

        try {
            const stringValue = JSON.stringify(value);

            if (!this.isValueSizeAcceptable(stringValue, key)) {
                return;
            }

            localStorage.setItem(key, stringValue);
        } catch (e) {
            this.handleStorageError(e, key, value);
        }
    }

    private shouldStoreKey(key: string): boolean {
        const isBlocked = this.config.blockedPatterns.some(pattern => key.includes(pattern));
        if (isBlocked && this.config.enableLogging) {
            console.log(`Skipping cache for blocked key pattern: ${key}`);
        }
        return !isBlocked;
    }

    private isValueSizeAcceptable(stringValue: string, key: string): boolean {
        if (stringValue.length > this.config.maxValueSize) {
            if (this.config.enableLogging) {
                console.warn(`Skipping cache for large value (${this.formatBytes(stringValue.length)}) for key: ${key}`);
            }
            return false;
        }
        return true;
    }

    private handleStorageError(error: any, key: string, value: any): void {
        if (error.name === 'QuotaExceededError' || error.code === 22) {
            this.handleQuotaExceeded(key, value);
        } else {
            console.error('Error storing data in localStorage:', error);
        }
    }

    private handleQuotaExceeded(key: string, value: any): void {
        if (this.config.enableLogging) {
            const valueSize = JSON.stringify(value).length;
            console.warn('LocalStorage quota exceeded.');
            console.log(`Attempted to store key: ${key} (${this.formatBytes(valueSize)})`);
        }

        this.clearOldCacheData();
        this.retryStorage(key, value);
    }

    private retryStorage(key: string, value: any): void {
        try {
            const stringValue = JSON.stringify(value);
            if (stringValue.length <= this.config.retryMaxSize) {
                localStorage.setItem(key, stringValue);
                if (this.config.enableLogging) {
                    console.log('Successfully stored after cache clear');
                }
            } else if (this.config.enableLogging) {
                console.warn(`Value too large to store even after clearing cache (${this.formatBytes(stringValue.length)})`);
            }
        } catch (retryError) {
            console.error('Failed to store in localStorage even after clearing cache:', retryError);
            this.emergencyCacheClear();
        }
    }

    private emergencyCacheClear(): void {
        if (this.config.enableLogging) {
            console.warn('Performing emergency localStorage clear as last resort');
        }
        try {
            localStorage.clear();
        } catch (clearError) {
            console.error('Failed to clear localStorage:', clearError);
        }
    }

    private formatBytes(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    public get(key: string): any {
        // Don't try to get blocked keys since we don't store them
        if (!this.shouldStoreKey(key)) {
            return null;
        }

        try {
            const stringifiedObject = localStorage.getItem(key);
            return stringifiedObject ? JSON.parse(stringifiedObject) : null;
        } catch (error) {
            this.handleCorruptedCacheEntry(key, error);
            return null;
        }
    }

    public delete(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            if (this.config.enableLogging) {
                console.warn('Failed to delete cache entry:', key, error);
            }
        }
    }

    public hasLoadedUser(): boolean {
        return !!localStorage.getItem(USER_STATE_KEY);
    }

    public shouldCache(): boolean {
        return this.cacheEnabled;
    }

    public getCacheStats(): { keys: number; estimatedSize: string } {
        let totalSize = 0;
        let keyCount = 0;

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
                keyCount++;
                const value = localStorage.getItem(key);
                if (value) {
                    totalSize += key.length + value.length;
                }
            }
        }

        return {
            keys: keyCount,
            estimatedSize: this.formatBytes(totalSize * 2) // Rough estimate (UTF-16)
        };
    }

    public updateConfig(partialConfig: Partial<CacheConfig>): void {
        this.config = { ...this.config, ...partialConfig };
        if (this.config.enableLogging) {
            console.log('Cache configuration updated:', partialConfig);
        }
    }

    public getConfig(): CacheConfig {
        return { ...this.config }; // Return a copy to prevent external modification
    }

    public clearAllCache(): void {
        try {
            localStorage.clear();
            if (this.config.enableLogging) {
                console.log('All localStorage data cleared');
            }
        } catch (error) {
            console.error('Failed to clear localStorage:', error);
        }
    }

    private handleCorruptedCacheEntry(key: string, error: any): void {
        if (this.config.enableLogging) {
            console.warn('Failed to parse cached data for key:', key, error);
        }

        try {
            localStorage.removeItem(key);
        } catch (removeError) {
            if (this.config.enableLogging) {
                console.warn('Failed to remove corrupted cache entry:', key, removeError);
            }
        }
    }

    private clearOldCacheData(): void {
        const cacheKeys = this.getCacheableKeys();
        const keysToRemove = Math.max(1, Math.floor(cacheKeys.length / 2));

        if (this.config.enableLogging) {
            console.log(`Clearing ${keysToRemove} cache entries to free space`);
        }

        cacheKeys.slice(0, keysToRemove).forEach(key => {
            try {
                localStorage.removeItem(key);
                if (this.config.enableLogging) {
                    console.log('Cleared cache key:', key);
                }
            } catch (error) {
                if (this.config.enableLogging) {
                    console.warn('Failed to clear cache key:', key, error);
                }
            }
        });
    }

    private getCacheableKeys(): string[] {
        const cacheableKeys = [];
        const cachePatterns = ['_CACHE', '_STATE', 'DASHBOARD', 'LOG'];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && cachePatterns.some(pattern => key.includes(pattern))) {
                cacheableKeys.push(key);
            }
        }

        return cacheableKeys;
    }
}